import { type ParsedUrlQuery } from 'querystring'
import { anyPass, defaultTo, isEmpty, isNil } from 'ramda'
import { type ClientError } from 'shared/api/fetch'
import { formatToPercent } from 'shared/numberFormat'
import { type Entries } from 'type-fest'
import { promisify } from 'util'
import { addDays, formatDateToString, isBefore, isSameDay } from 'shared/dateFns'
import { ALVIE_HELP_CENTER_URL, ALVIE_SUPPORT_EMAIL, ALVIE_DATASOURCE_KEY } from 'shared/constants'
import { type IntercomArticleId } from 'shared/constants/documentation'
import { type IntercomErrorId } from 'shared/configStatuses'
import { makeErrorToast, makeSuccessToast } from 'shared/components/Toaster'
import toSnakeCase from 'snakecase-keys'
import {
  DIMENSION_OPERATORS,
  METRIC_OPERATORS,
  normalizeMetricOperator,
  type OperatorType
} from 'shared/constants/filterOperators'
import { type TFunction } from 'i18next'
import { type GroupedRecommendationItem } from 'src/recommendations/types'
import { secureRandomFloat } from './secureRandom'

export { get as getFromStore, remove as removeToStore, store as saveToStore } from '../lstore'

export * from './stringFormatter'

export const noop = (): void => {}

export const noopAsync = async (): Promise<void> => {}

export const pickRandom = <T>(items: readonly T[]): T => items[Math.floor(secureRandomFloat() * items.length)]

export const reloadCurrentPage = (): void => {
  if (typeof window !== 'undefined') {
    window.location.reload()
  }
}

export const redirectToPage = (url: string): void => {
  if (typeof window !== 'undefined') {
    window.location.assign(url)
  }
}
export const pause = promisify((arg: number, cb: () => void) => setTimeout(cb, arg))

export const assertApiReqRes = (req: unknown, res: unknown): void => {
  if (req == null) {
    throw new Error('request is not available')
  }
  if (res == null) {
    throw new Error('response is not available')
  }
}

export const openNewTab = (url: string): void => {
  window.open(url, '_blank')
}

export const getReturnTo = (returnTo?: string | null): string => {
  const currentLocation = window.location.toString()
  return returnTo ?? currentLocation.replace(new URL(currentLocation).origin, '')
}

const SAFE_RETURN_TO_PATH = /^\/[\w\-./?&=%+:@]*$/

export const isValidReturnToUrl = (url: string | undefined, baseUrl: string): boolean => {
  if (url == null || url === '') {
    return false
  }

  // Only in-app relative paths are allowed for post-auth redirects.
  if (!url.startsWith('/') || url.startsWith('//')) {
    return false
  }

  if (!SAFE_RETURN_TO_PATH.test(url)) {
    return false
  }

  try {
    const base = new URL(baseUrl)
    const resolved = new URL(url, baseUrl)

    return resolved.origin === base.origin
  } catch {
    return false
  }
}

const parseValidatedReturnTo = (
  returnTo: string,
  baseUrl: string
): { pathname: string; queryParams: ParsedUrlQuery } => {
  const urlObj = new URL(returnTo, baseUrl)
  const urlParams = new URLSearchParams(urlObj.search)
  const params: Record<string, string> = {}

  for (const param of urlParams) {
    params[param[0]] = param[1]
  }

  return {
    queryParams: params,
    pathname: urlObj.pathname
  }
}

export const parseSafeReturnToUrl = (
  url: string | undefined,
  baseUrl: string
): { queryParams: ParsedUrlQuery; pathname: string } | null => {
  if (!isValidReturnToUrl(url, baseUrl)) {
    return null
  }

  return parseValidatedReturnTo(url as string, baseUrl)
}

export const buildReturnToQueryParam = (safeReturnTo: { pathname: string; queryParams: ParsedUrlQuery }): string => {
  const params = new URLSearchParams()

  Object.entries(safeReturnTo.queryParams).forEach(([key, value]) => {
    if (typeof value === 'string') {
      params.set(key, value)
    }
  })

  const query = params.toString()
  return query.length > 0 ? `${safeReturnTo.pathname}?${query}` : safeReturnTo.pathname
}

type ChangePathFn = (pathname: string, queryParams?: ParsedUrlQuery) => void | Promise<void>

export const redirectToSafeReturnTo = (
  changePath: ChangePathFn,
  returnTo: string | undefined,
  baseUrl: string,
  fallbackPath: string
): void => {
  if (!isValidReturnToUrl(returnTo, baseUrl)) {
    void changePath(fallbackPath)
    return
  }

  const { pathname, queryParams } = parseValidatedReturnTo(returnTo as string, baseUrl)
  void changePath(pathname, queryParams)
}

export const defaultToZero = defaultTo(0)

export const calDifferenceRelativeToPrevious = (current: number, previous: number): number => {
  return (current - previous) / (previous !== 0 ? previous : 1)
}

export const isClientEnvironment = (): boolean => typeof window !== 'undefined'

export const objectKeys = <T extends Record<string, unknown>>(obj: T): Array<keyof T> =>
  Object.keys(obj) as Array<keyof T>

/**
 * Type-safe Object.entries
 */
export const objectEntries = <T extends object>(obj: T): Entries<T> => Object.entries(obj) as Entries<T>

/**
 * Type-safe Object.fromEntries
 */
export const objectFromEntries = <T extends ReadonlyArray<readonly [PropertyKey, unknown]>>(
  entries: T
): { [K in T[number] as K[0]]: K[1] } => {
  return Object.fromEntries(entries) as { [K in T[number] as K[0]]: K[1] }
}

export const calculateRoas = (revenue: number, spend: number): number => {
  return spend === 0 || revenue === 0 ? 0 : revenue / spend
}

export const calculateCostPerAction = (spend: number, conversion: number): number => {
  return conversion === 0 || spend === 0 ? 0 : spend / conversion
}

export const calculateAverageOverValue = (revenue: number, conversion: number): number => {
  return conversion === 0 ? 0 : revenue / conversion
}

export const calculateCostPerClick = (spend: number, clicks: number): number => {
  return clicks === 0 ? 0 : spend / clicks
}

export const calculateCostOfSales = (spend: number, revenue: number): number => {
  return revenue === 0 ? 0 : spend / revenue
}

export const calculateConversionRate = (conversion: number, clicks: number): number => {
  return clicks === 0 ? 0 : conversion / clicks
}

export const calculateClickThroughRate = (clicks: number, impressions: number): number => {
  return impressions === 0 ? 0 : clicks / impressions
}

export const calculateCostPerThousandImpressions = (spend: number, impressions: number): number => {
  return impressions === 0 ? 0 : spend / (impressions / 1000)
}

export const calculateVideoCompletionRate = (videoCompletions: number, videoViews: number): number => {
  return videoViews === 0 ? 0 : videoCompletions / videoViews
}

export const calculateAvgTimeWatchedActions = (videoAvgTimeWatchedActions: number, numberOfRecords: number): number => {
  return numberOfRecords === 0 ? 0 : videoAvgTimeWatchedActions / numberOfRecords
}

export const calculateHookRate = (videoViews: number, impressions: number): number => {
  return impressions === 0 ? 0 : videoViews / impressions
}

export const calculateHoldRate = (qualifiedVideoViews: number, videoViews: number): number => {
  return videoViews === 0 ? 0 : qualifiedVideoViews / videoViews
}

export const calculateViewThroughRate = (videoP100WatchedActions: number, impressions: number): number => {
  return impressions === 0 ? 0 : videoP100WatchedActions / impressions
}

export const calculateCostPerVideoView = (spend: number, videoViews: number): number => {
  return videoViews === 0 ? 0 : spend / videoViews
}

export const calculateCostPerQualifiedVideoView = (spend: number, qualifiedVideoViews: number): number => {
  return qualifiedVideoViews === 0 ? 0 : spend / qualifiedVideoViews
}

export const calculateRemainingBudget = (value: number, budget: number | null): number | null => {
  if (budget === null) {
    return null
  }
  return budget - value
}

export const calculateBudgetDepletion = (value: number, budget: number | null): number | null => {
  if (budget === null) {
    return null
  }
  return budget === 0 ? 0 : value / budget
}

export const isNilOrEmpty = (value: unknown): value is null | undefined => anyPass([isNil, isEmpty])(value)

export const copyToClipboard = async (text: string): Promise<void> => {
  await navigator.clipboard.writeText(text)
}

export const pasteFromClipboard = async (): Promise<string> => {
  return await navigator.clipboard.readText()
}

export const transformCamelToSnakeCase = (str: string, options: { shouldSeparateNumbers?: boolean } = {}): string =>
  Object.keys(
    toSnakeCase(
      { [str]: true },
      {
        ...(options.shouldSeparateNumbers === true && {
          parsingOptions: {
            stripRegexp: /(?<=[a-zA-Z])(?=\d)/g
          }
        })
      }
    )
  )[0]

export const isClientMismatchError = (error: ClientError): boolean => {
  return error.message.includes('Client ID does not match')
}

export const getBigQueryTableDeepLink = (projectId: string, datasetId: string, tableId: string): string => {
  return `https://console.cloud.google.com/bigquery?project=${projectId}&p=${projectId}&d=${datasetId}&t=${tableId}&page=table`
}

export const sortOptionInAlphabeticalOrder = <T extends { label: string }>(a: T, b: T): number =>
  a.label.localeCompare(b.label)

export const sortOptionAlvieFirst = <T extends { label: string; subCategory?: string | { label: string } }>(
  a: T,
  b: T
): number => {
  const subCategoryA = typeof a.subCategory === 'string' ? a.subCategory : a.subCategory?.label
  const subCategoryB = typeof b.subCategory === 'string' ? b.subCategory : b.subCategory?.label
  const isAlvieA = subCategoryA === ALVIE_DATASOURCE_KEY
  const isAlvieB = subCategoryB === ALVIE_DATASOURCE_KEY

  if (isAlvieA && !isAlvieB) {
    return -1
  }
  if (!isAlvieA && isAlvieB) {
    return 1
  }

  // If both are 'alvie' or neither are, sort them alphabetically.
  return a.label.localeCompare(b.label)
}

export const getQueryParamsAndPathnameFromUrl = (
  url: string,
  baseUrl: string
): { queryParams: ParsedUrlQuery; pathname: string } => {
  const urlObj = new URL(url, baseUrl)
  const urlParams = new URLSearchParams(urlObj?.search)

  const params: Record<string, string> = {}
  for (const param of urlParams) {
    params[param[0]] = param[1]
  }

  return {
    queryParams: params,
    pathname: urlObj?.pathname
  }
}

export type PercentageStatus = 'positive' | 'negative' | 'equal'
type CustomSymbol = '▲+' | '▼-' | '⬤ '

const customSymbol: Record<PercentageStatus, CustomSymbol> = {
  positive: '▲+',
  negative: '▼-',
  equal: '⬤ '
}

export const getPercentageDisplayValueAndStatus = (
  presentValue: number,
  previousValue: number
): {
  displayValue: string
  percentageStatus: PercentageStatus
} => {
  let displayValue: string
  let percentageStatus: PercentageStatus

  if (previousValue === 0 && presentValue !== 0) {
    displayValue = '▲+N/A'
    percentageStatus = 'positive'
  } else {
    const ratioDifference: number = calDifferenceRelativeToPrevious(presentValue, previousValue)

    if (Math.abs(ratioDifference) < 0.01) {
      percentageStatus = 'equal'
    } else {
      percentageStatus = ratioDifference > 0 ? 'positive' : 'negative'
    }

    const symbolPrefix = customSymbol[percentageStatus]
    displayValue = `${symbolPrefix}${formatToPercent(Math.abs(ratioDifference))}`
  }
  return { displayValue, percentageStatus }
}

export const getCopyConfigDetails = (
  query: ParsedUrlQuery
): {
  configId: number
  isCopyConfig: boolean
} => {
  const configId = typeof query?.from === 'string' ? parseInt(query.from, 10) : NaN
  const isCopyConfig = !isNaN(configId)
  return {
    configId,
    isCopyConfig
  }
}

export const isNewNonCopyConfigCheck = ({
  isNewConfig,
  isCopyConfig
}: {
  isNewConfig: boolean
  isCopyConfig: boolean
}): boolean => isNewConfig && !isCopyConfig

export const validateDataset = (dataset: string): boolean => {
  const stringValue = dataset
  const startsWithUnderscore = stringValue.startsWith('_')

  const containsOnlyAlphanumericAndUnderscore = /^\w*$/.test(stringValue)

  return stringValue.length <= 1024 && containsOnlyAlphanumericAndUnderscore && !startsWithUnderscore
}

export const validateDomainName = (value: string): boolean => /^[a-z0-9]+([-.][a-z0-9]+)*\.[a-z]{2,6}$/.test(value)

export const validateEmail = (value: string): boolean =>
  /^[a-z0-9]+([-._][a-z0-9]+)*@[a-z0-9]+([-.][a-z0-9]+)*\.[a-z]{2,6}$/.test(value)

export const validateHexadecimal = (value: string): boolean => /^[0-9a-f]+$/.test(value)

export const validateTablePrefix = (tablePrefix: string): boolean => /^\w*$/.test(tablePrefix)

export const throwIfUndefined = <T>(value: T | undefined): T => {
  if (value === undefined) {
    throw new Error('Value is undefined')
  }
  return value
}

/**
 * Validates if a string is a valid RE2 regex string.
 */
export const isValidRE2RegexString = (value: string): boolean => {
  try {
    // Basic RegEx validation by compiling the string into a RegExp object.
    // BigQuery RE2 syntax allows various flags (i, g, m, s, u) to be added to the beginning of the regex,
    // which are not supported by the RegExp constructor, so they are striupped out here.
    RegExp(value.replace(/^\(\?[igmsu]+\)?/, ''))

    // Vvalidate the BigQuery RE2 flags (any combination without repetition is valid)
    if (value.startsWith('(?')) {
      if (!/^\(\?[igmsu]{1,5}\)/.test(value)) {
        return false
      }
      if (/^\(\?[igmsu]*([igmsu])[igmsu]*\1/.test(value)) {
        return false
      }
    }

    // Length check (Limit to 1000 characters to prevent too complex queries)
    if (value.length > 1000) {
      return false
    }

    // Additional checks for BigQuery compatibility
    const backreferenceRegex = /\\\d/
    const lookbehindRegex = /\(\?<=/
    const negativeLookbehindRegex = /\(\?<!/
    const unsupportedEscapeSequence = /\\v/
    const disallowedPatterns = [backreferenceRegex, lookbehindRegex, negativeLookbehindRegex, unsupportedEscapeSequence]

    for (const pattern of disallowedPatterns) {
      if (pattern.test(value)) {
        return false
      }
    }

    return true
  } catch (_) {
    return false
  }
}

export const generateCompleteDateRange = <T>({
  startDate,
  endDate,
  lineChartData,
  fillMissingLineChartData
}: {
  startDate: string
  endDate: string
  lineChartData: T[]
  fillMissingLineChartData: ({ allDates, lineChartData }: { allDates: string[]; lineChartData: T[] }) => T[]
}): T[] => {
  if (isNilOrEmpty(lineChartData)) {
    return []
  }

  const allDates = []
  let currentDate = new Date(startDate)
  const targetEndDate = new Date(endDate)

  while (isBefore(currentDate, targetEndDate) || isSameDay(currentDate, targetEndDate)) {
    allDates.push(formatDateToString({ date: currentDate, targetFormat: 'yyyy-MM-dd' }))
    currentDate = addDays(currentDate, 1)
  }

  return fillMissingLineChartData({ allDates, lineChartData })
}

export const scrollToElement = (element: HTMLElement): void => {
  element.scrollIntoView({ behavior: 'smooth' })
}

// Simple hash - works great in React
export const stringToHash = (str: string): number => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return hash
}

export const openMailToSupport = (prePopulatedMessage?: string, error?: Error): void => {
  let mailToUrl = `mailto:${ALVIE_SUPPORT_EMAIL}`
  if (prePopulatedMessage != null && prePopulatedMessage !== '') {
    mailToUrl += `?subject=Support%20Request&body=${encodeURIComponent(prePopulatedMessage)}`
  }
  openNewTab(mailToUrl)
}

export const getHelpCenterArticleUrl = (articleId: IntercomArticleId | IntercomErrorId): string =>
  `${ALVIE_HELP_CENTER_URL}/articles/${articleId}`

export const openHelpCenterArticleInNewTab = (articleId: IntercomArticleId | IntercomErrorId, error?: Error): void => {
  openNewTab(getHelpCenterArticleUrl(articleId))
}

export const handleCopy = async (text: string, successMessage: string, errorMessage: string): Promise<void> => {
  await copyToClipboard(text)
    .then(() => {
      makeSuccessToast(successMessage)
    })
    .catch(() => {
      makeErrorToast(errorMessage)
    })
}

export const createStringSorter =
  <T, K extends keyof T>(accessor: K) =>
  (rowA: { original: T }, rowB: { original: T }): number => {
    const valueA = String(rowA.original[accessor] ?? '')
    const valueB = String(rowB.original[accessor] ?? '')
    return valueA.localeCompare(valueB)
  }

export const handleRowValueValidation =
  ({ operator, t }: { operator: OperatorType; t: TFunction }) =>
  (inputValue: string): string | undefined => {
    if ([DIMENSION_OPERATORS.matches_regex.id, DIMENSION_OPERATORS.not_matches_regex.id].includes(operator)) {
      return isValidRE2RegexString(inputValue) ? undefined : t('invalidRegex')
    }
    if (Object.keys(METRIC_OPERATORS).includes(normalizeMetricOperator(operator))) {
      return isNaN(Number(inputValue)) ? t('invalidNumber') : undefined
    }
  }

export const isRowGreyed = (row: GroupedRecommendationItem): boolean => {
  return row.status === 'ignored' || row.status === 'ignoredUntil'
}

export const delayBetweenBatches = async (ms = 2000): Promise<void> => {
  await new Promise((resolve) => setTimeout(resolve, ms))
}
